/**
 * Pure data-manipulation utilities for the data page: sorting, filtering, JQ expression building.
 * Extracted here so they can be unit-tested independently of DOM logic.
 */

export type FilterOperator =
	| "eq"
	| "neq"
	| "gt"
	| "gte"
	| "lt"
	| "lte"
	| "contains"
	| "starts_with"
	| "ends_with"

export type ActiveFilter = {
	field: string
	operator: FilterOperator
	value: string
}

export const FILTER_OPERATOR_OPTIONS: { value: FilterOperator; label: string }[] = [
	{ value: "eq", label: "=" },
	{ value: "neq", label: "≠" },
	{ value: "gt", label: ">" },
	{ value: "gte", label: "≥" },
	{ value: "lt", label: "<" },
	{ value: "lte", label: "≤" },
	{ value: "contains", label: "contains" },
	{ value: "starts_with", label: "starts with" },
	{ value: "ends_with", label: "ends with" },
]

export function escapeJqStr(s: string): string {
	return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')
}

export function buildSingleFilterJq(f: ActiveFilter): string {
	const fieldRef = `.${f.field}`
	const num = parseFloat(f.value)
	const isNum = !isNaN(num)
	switch (f.operator) {
		case "eq":
			return isNum
				? `select(${fieldRef} == ${num})`
				: `select(${fieldRef} == "${escapeJqStr(f.value)}")`
		case "neq":
			return isNum
				? `select(${fieldRef} != ${num})`
				: `select(${fieldRef} != "${escapeJqStr(f.value)}")`
		case "gt":
			return `select(${fieldRef} > ${num})`
		case "gte":
			return `select(${fieldRef} >= ${num})`
		case "lt":
			return `select(${fieldRef} < ${num})`
		case "lte":
			return `select(${fieldRef} <= ${num})`
		case "contains":
			return `select(${fieldRef} | type == "string" and contains("${escapeJqStr(f.value)}"))`
		case "starts_with":
			return `select(${fieldRef} | startswith("${escapeJqStr(f.value)}"))`
		case "ends_with":
			return `select(${fieldRef} | endswith("${escapeJqStr(f.value)}"))`
	}
}

export function buildProjectionJq(
	keys: string[],
	sortKey?: string,
	sortAsc: boolean = true,
	filters: ActiveFilter[] = [],
): string {
	const hasKeys = keys.length > 0
	const hasSort = !!sortKey
	const hasFilters = filters.length > 0
	if (!hasKeys && !hasSort && !hasFilters) return ""

	const filterExprs = hasFilters ? filters.map(buildSingleFilterJq) : []

	if (hasSort) {
		let expr = `sort_by(.${sortKey})`
		if (!sortAsc) expr += " | reverse"
		expr += " | .[]"
		if (hasFilters) expr += " | " + filterExprs.join(" | ")
		if (hasKeys) expr += ` | { ${keys.join(", ")} }`
		return expr
	}

	// No sort: iterate, filter, project
	let expr = ".[]"
	if (hasFilters) expr += " | " + filterExprs.join(" | ")
	if (hasKeys) expr += ` | { ${keys.join(", ")} }`
	return expr
}

export function sortRows<T extends Record<string, unknown>>(
	rows: T[],
	key: string,
	asc: boolean,
): T[] {
	return [...rows].sort((a, b) => {
		const va = a[key]
		const vb = b[key]
		const aNum = typeof va === "number" ? va : NaN
		const bNum = typeof vb === "number" ? vb : NaN
		if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
			return asc ? aNum - bNum : bNum - aNum
		}
		const aStr = va != null ? String(va) : ""
		const bStr = vb != null ? String(vb) : ""
		const cmp = aStr.localeCompare(bStr, undefined, { sensitivity: "base" })
		return asc ? cmp : -cmp
	})
}

export function applyFilters<T extends Record<string, unknown>>(
	rows: T[],
	filters: ActiveFilter[],
): T[] {
	if (filters.length === 0) return rows
	return rows.filter((row) =>
		filters.every((f) => {
			const val = row[f.field]
			const strVal = val != null ? String(val).toLowerCase() : ""
			const numVal = typeof val === "number" ? val : parseFloat(String(val))
			const filterNum = parseFloat(f.value)
			switch (f.operator) {
				case "eq":
					return !isNaN(numVal) && !isNaN(filterNum)
						? numVal === filterNum
						: strVal === f.value.toLowerCase()
				case "neq":
					return !isNaN(numVal) && !isNaN(filterNum)
						? numVal !== filterNum
						: strVal !== f.value.toLowerCase()
				case "gt":
					return !isNaN(numVal) && !isNaN(filterNum) && numVal > filterNum
				case "gte":
					return !isNaN(numVal) && !isNaN(filterNum) && numVal >= filterNum
				case "lt":
					return !isNaN(numVal) && !isNaN(filterNum) && numVal < filterNum
				case "lte":
					return !isNaN(numVal) && !isNaN(filterNum) && numVal <= filterNum
				case "contains":
					return strVal.includes(f.value.toLowerCase())
				case "starts_with":
					return strVal.startsWith(f.value.toLowerCase())
				case "ends_with":
					return strVal.endsWith(f.value.toLowerCase())
			}
		}),
	)
}
